import React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, LinearProgress, Box } from '@mui/material';
import { CheckCircle, Link as LinkIcon } from '@mui/icons-material';
import useAutoLink from '../hooks/useAutoLink';

export default function AutoLinkDialog({ open, onClose, retailerId }) {
  const { autoLink, progress, isLinking } = useAutoLink();

  const handleAutoLink = async () => {
    await autoLink(retailerId);
  };

  const handleClose = () => {
    if (!isLinking) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Auto-Link Products</DialogTitle>
      <DialogContent>
        <Typography variant="body1" gutterBottom>
          Automatically match your products to our master catalog using AI-powered matching.
        </Typography>

        <Typography variant="body2" color="text.secondary" paragraph>
          This will:
        </Typography>
        <ul style={{ marginTop: 0 }}>
          <li>
            <Typography variant="body2" color="text.secondary">
              Match products by UPC codes
            </Typography>
          </li>
          <li>
            <Typography variant="body2" color="text.secondary">
              Use fuzzy name matching for products without barcodes
            </Typography>
          </li>
          <li>
            <Typography variant="body2" color="text.secondary">
              Provide access to product images and nutrition data
            </Typography>
          </li>
        </ul>

        {isLinking && progress && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="body2" gutterBottom>
              Linking products... {progress.linked} of {progress.total}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={progress.total > 0 ? (progress.linked / progress.total) * 100 : 0}
            />
          </Box>
        )}

        {progress?.completed && (
          <Box sx={{ mt: 3, p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <CheckCircle color="success" />
              <Typography variant="h6">
                Successfully linked {progress.linked} products!
              </Typography>
            </Box>
            {progress.unmatched > 0 && (
              <Typography variant="body2">
                {progress.unmatched} products could not be matched automatically and may need manual linking.
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isLinking}>
          {progress?.completed ? 'Close' : 'Cancel'}
        </Button>
        {!progress?.completed && (
          <Button
            variant="contained"
            onClick={handleAutoLink}
            disabled={isLinking}
            startIcon={<LinkIcon />}
          >
            Start Auto-Link
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
